import React, { useEffect, useState } from "react";
import {
  Form,
  useLoaderData,
  redirect,
  useOutletContext,
  Link,
} from "react-router-dom";
import { toast } from "react-toastify";
import Wrapper from "../assets/wrappers/AddCampaignWrapper";
import Switch from "@mui/material/Switch";
import {
  FormRow,
  SubmitBtn,
  FormRowSelectId,
  FormRowSelect,
  FormRowBuildings,
  NavLinkButton,
} from "../components";
import { SCREEN_TYPE } from "../../../utils/constants.js";
import { Campaign } from "../components/Campaign";
import { customFetch } from "../utils/custom-fetch";

/* ---------------- loader ---------------- */
export const loader = async ({ params }) => {
  try {
    const [
      { data: campaigns },
      { data: cities },
      { data: maintenanceCompanies },
    ] = await Promise.all([
      customFetch.get("/campaigns?status=פעיל&limit=9999"),
      customFetch.get("/campaigns/cities-streets-buildings"),
      customFetch.get("/buildings/maintenance-companies"),
    ]);

    let schedule = null;

    if (params.scheduleId) {
      const { data } = await customFetch.get(
        `/campaign-schedules/${params.scheduleId}`
      );

      schedule = data;
    }

    return { campaigns, cities, maintenanceCompanies, schedule };
  } catch {
    toast.error("שגיאה בטעינת הדף");
    return redirect("/dashboard");
  }
};

/* ---------------- action ---------------- */
export const action = async ({ request, params }) => {
  const fd = await request.formData();
  const body = {
    campaignId: fd.get("campaignId"),
    startDate: fd.get("startDate"),
    endDate: fd.get("endDate"),
    screenType: fd.get("screenType"),
    durationInSeconds: Number(fd.get("durationInSeconds")),
    isGlobal: fd.get("isGlobal") === "true",
    buildingIds: fd.getAll("buildingIds"),
    timeSlots: [{ start: fd.get("startTime"), end: fd.get("endTime") }],
  };

  const errors = [];
  if (!body.campaignId) errors.push("חובה לבחור קמפיין");
  if (!body.startDate || !body.endDate) errors.push("תאריכים נדרשים");
  if (!fd.get("startTime") || !fd.get("endTime")) errors.push("שעות נדרשות");
  if (body.durationInSeconds < 2) errors.push("משך מינימלי הוא 2 שניות");
  if (!body.isGlobal && body.buildingIds.length === 0)
    errors.push("בחר בניינים");

  if (errors.length) {
    toast.error(errors.join(" | "));
    return null;
  }

  try {
    if (params.scheduleId) {
      await customFetch.patch(`/campaign-schedules/${params.scheduleId}`, body);
      toast.success("התזמון עודכן בהצלחה");
    } else {
      await customFetch.post("/campaign-schedules", body);
      toast.success("התזמון נוצר בהצלחה");
    }

    return redirect("/dashboard/all-buildings");
  } catch (err) {
    toast.error(err?.response?.data?.msg || "שגיאה בשמירה");
    return null;
  }
};

const CampaignScheduler = () => {
  const {
    campaigns,
    cities: initialCities,
    maintenanceCompanies,
    schedule,
  } = useLoaderData();

  const { user } = useOutletContext();
  const today = new Date().toISOString().slice(0, 10);

  const [cities, setCities] = useState(initialCities);
  const [allCities] = useState(initialCities);

  // Initialize formData based on whether we're editing or creating
  const initializeFormData = () => {
    if (schedule?.schedule) {
      // Edit mode - populate with schedule data
      const scheduleData = schedule.schedule;
      const campaignId =
        typeof scheduleData.campaignId === "object"
          ? scheduleData.campaignId._id
          : scheduleData.campaignId;

      return {
        campaignId: campaignId || "",
        screenType: scheduleData.screenType || "מסך ראשי",
        startDate: scheduleData.startDate?.split("T")[0] || today,
        endDate: scheduleData.endDate?.split("T")[0] || "",
        startTime: scheduleData.timeSlots?.[0]?.start || "",
        endTime: scheduleData.timeSlots?.[0]?.end || "",
        duration: scheduleData.durationInSeconds || 10,
        selectedBuildings: scheduleData.buildingIds || [],
        selectedCity: "",
        selectedStreet: "",
        maintenanceCompany: "הכל",
        isGlobal: scheduleData.isGlobal || false,
      };
    } else {
      // Create mode - start with empty form
      return {
        campaignId: "",
        screenType: "מסך ראשי",
        startDate: today,
        endDate: "",
        startTime: "",
        endTime: "",
        duration: 10,
        selectedBuildings: [],
        selectedCity: "",
        selectedStreet: "",
        maintenanceCompany: "הכל",
        isGlobal: false,
      };
    }
  };

  const [formData, setFormData] = useState(initializeFormData);
  const isEditMode = Boolean(schedule?.schedule);

  const getFilteredBuildings = (state) => {
    if (state.selectedCity && state.selectedCity !== "כל הערים") {
      const cityObj = cities.find((c) => c.city === state.selectedCity);
      if (!cityObj) return [];
      if (state.selectedStreet) {
        const streetObj = cityObj.streets.find(
          (s) => s.street === state.selectedStreet
        );
        return streetObj ? streetObj.buildings : [];
      }
      return cityObj.streets.flatMap((s) => s.buildings);
    }
    return cities.flatMap((c) => c.streets.flatMap((s) => s.buildings));
  };

  // Effect for filtering cities based on maintenance company and location
  useEffect(() => {
    if (formData.isGlobal) return;

    const params = new URLSearchParams();
    if (formData.maintenanceCompany && formData.maintenanceCompany !== "הכל") {
      const isObjectId = /^[0-9a-fA-F]{24}$/.test(formData.maintenanceCompany);
      if (!isObjectId) {
        toast.error("ערך חברת האחזקה אינו ObjectId");
        return;
      }
      params.append("maintenanceCompanyId", formData.maintenanceCompany);
      params.append("createdBy", formData.maintenanceCompany);
    }
    if (formData.selectedCity && formData.selectedCity !== "הכל") {
      params.append("city", formData.selectedCity);
    }
    if (formData.selectedStreet && formData.selectedStreet !== "הכל") {
      params.append("street", formData.selectedStreet);
    }

    (async () => {
      try {
        const { data } = await customFetch.get(
          `/campaigns/cities-streets-buildings?${params.toString()}`
        );
        setCities(data);
      } catch {
        toast.error("שגיאה בטעינת ערים");
      }
    })();
  }, [
    formData.maintenanceCompany,
    formData.selectedCity,
    formData.selectedStreet,
    formData.isGlobal,
  ]);

  // Effect for auto-populating campaign data (only in create mode)
  useEffect(() => {
    // Only run this effect in create mode and when campaignId changes
    if (isEditMode || !formData.campaignId) return;

    const camp = campaigns.campaigns.find((c) => c._id === formData.campaignId);
    if (!camp) return;

    setFormData((prev) => ({
      ...prev,
      startDate: camp.startDate?.split("T")[0] || today,
      endDate: camp.endDate?.split("T")[0] || "",
      startTime: camp.displayHours?.start || "",
      endTime: camp.displayHours?.end || "",
      duration: camp.duration || 15,
      isGlobal: camp.isGlobal || false,
    }));
  }, [formData.campaignId, isEditMode, campaigns, today]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "selectedCity" || name === "selectedStreet") {
        updated.selectedBuildings = getFilteredBuildings(updated).map(
          (b) => b._id
        );
      }
      return updated;
    });
  };

  const handleBuildingSelection = (id) => {
    setFormData((p) => ({
      ...p,
      selectedBuildings: p.selectedBuildings.includes(id)
        ? p.selectedBuildings.filter((x) => x !== id)
        : [...p.selectedBuildings, id],
    }));
  };

  const handleSelectAll = () => {
    const all = getFilteredBuildings(formData).map((b) => b._id);
    setFormData((p) => ({
      ...p,
      selectedBuildings: p.selectedBuildings.length === all.length ? [] : all,
    }));
  };

  const filteredBuildings = getFilteredBuildings(formData);
  const selectedCampaign = campaigns.campaigns.find(
    (c) => c._id === formData.campaignId
  );

  return (
    <>
      {selectedCampaign && (
        <div className="full-page">
          <Campaign {...selectedCampaign} />
        </div>
      )}

      <Wrapper className="full-page">
        <Form method="post" className="form">
          <h2 className="form-title">
            {isEditMode ? "עריכת תזמון קמפיין" : "הוספת תזמון לקמפיין"}
          </h2>

          <div className="form-center">
            <FormRowSelectId
              name="campaignId"
              labelText="בחר קמפיין"
              list={campaigns.campaigns}
              idField="_id"
              displayField="name"
              value={formData.campaignId}
              defaultValue={formData.campaignId}
              handleChange={handleChange}
            />

            <FormRowSelect
              name="screenType"
              labelText="באיזה מסך לפרסם"
              list={["בחר מסך", ...Object.values(SCREEN_TYPE)]}
              value={formData.screenType}
              defaultValue={formData.screenType}
              handleChange={handleChange}
            />

            <FormRow
              type="date"
              name="startDate"
              labelText="תאריך התחלה"
              value={formData.startDate}
              defaultValue={formData.startDate}
              handleChange={handleChange}
            />

            <FormRow
              type="date"
              name="endDate"
              labelText="תאריך סיום"
              value={formData.endDate}
              defaultValue={formData.endDate}
              handleChange={handleChange}
            />

            <FormRow
              type="time"
              name="startTime"
              labelText="שעת התחלה"
              value={formData.startTime}
              defaultValue={formData.startTime}
              handleChange={handleChange}
            />

            <FormRow
              type="time"
              name="endTime"
              labelText="שעת סיום"
              value={formData.endTime}
              defaultValue={formData.endTime}
              handleChange={handleChange}
            />

            <FormRow
              type="number"
              name="durationInSeconds"
              labelText="משך (שניות)"
              value={formData.duration}
              defaultValue={formData.duration}
              min={2}
              handleChange={handleChange}
            />

            {user.role === "admin" && (
              <div className="form-row">
                <label className="form-label">פרסום בכל המסכים</label>
                <Switch
                  checked={formData.isGlobal}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, isGlobal: e.target.checked }))
                  }
                />
                <input
                  type="hidden"
                  name="isGlobal"
                  value={formData.isGlobal}
                />
              </div>
            )}

            {user.role === "admin" && !formData.isGlobal && (
              <FormRowSelectId
                labelText="חברת אחזקה"
                name="maintenanceCompany"
                value={formData.maintenanceCompany}
                defaultValue={formData.maintenanceCompany}
                handleChange={handleChange}
                idField="value"
                displayField="name"
                list={maintenanceCompanies}
              />
            )}

            {!formData.isGlobal && (
              <FormRowSelectId
                name="selectedCity"
                labelText="בחר עיר"
                list={allCities}
                idField="city"
                displayField="city"
                value={formData.selectedCity}
                handleChange={handleChange}
              />
            )}

            {formData.selectedCity && !formData.isGlobal && (
              <FormRowSelectId
                name="selectedStreet"
                labelText="בחר רחוב"
                list={
                  cities.find((c) => c.city === formData.selectedCity)
                    ?.streets || []
                }
                idField="street"
                displayField="street"
                value={formData.selectedStreet}
                handleChange={handleChange}
              />
            )}
          </div>

          {!formData.isGlobal && filteredBuildings.length > 0 && (
            <FormRowBuildings
              buildings={filteredBuildings}
              selectedBuildings={formData.selectedBuildings}
              handleBuildingSelection={handleBuildingSelection}
              handleSelectAll={handleSelectAll}
            />
          )}

          {formData.selectedBuildings.map((id) => (
            <input key={id} type="hidden" name="buildingIds" value={id} />
          ))}

          <div className="btn-container">
            <SubmitBtn buttonText={isEditMode ? "עדכן" : "שמור"} />
          </div>
          <NavLinkButton text="ביטול" path="/dashboard/all-schedules" color={'gray'} />
        </Form>
      </Wrapper>
    </>
  );
};

export default CampaignScheduler;
